import React, { useState, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import UserContext from "../../../context/UserContext";
import { loginUser as apiLoginUser } from "../../../api/api";
import {
  sanitizeInput,
  isValidEmail,
  loginRateLimiter,
} from "../../../utils/security";
import "./InternalLogin.css";

const InternalLogin = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const { loginUser } = useContext(UserContext);
  const navigate = useNavigate();
  const location = useLocation();

  // Get the intended destination or default to dev docs
  const from = location.state?.from?.pathname || "/internal-dev-setup-d8f3a2c1";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Validate email format
    if (!isValidEmail(email)) {
      setError("Please enter a valid email address.");
      return;
    }

    // Check rate limiting
    const rateLimitCheck = loginRateLimiter.check(email);
    if (!rateLimitCheck.allowed) {
      setError(rateLimitCheck.message);
      return;
    }

    setIsLoading(true);

    try {
      // Sanitize inputs before sending
      const sanitizedEmail = sanitizeInput(email.toLowerCase().trim());

      // Call backend API
      const response = await apiLoginUser(sanitizedEmail, password);

      // Check if user has employee or admin role (data is nested in response.data)
      if (
        response.data?.role === "EMPLOYEE" ||
        response.data?.role === "ADMIN"
      ) {
        // Login successful
        loginUser({
          userId: response.data.userId,
          email: response.data.email,
          username:
            response.data.username ||
            response.data.firstname ||
            response.data.email,
          role: response.data.role,
          token: response.data.token,
          loginTime: new Date().toISOString(),
        });

        // Reset rate limiter on successful login
        loginRateLimiter.reset(email);

        // Redirect to the intended page
        navigate(from, { replace: true });
      } else {
        setError("Access denied. Insufficient privileges.");
        setIsLoading(false);
      }
    } catch (err) {
      // Generic error message to avoid information leakage
      setError("Invalid credentials. Please try again.");
      setIsLoading(false);
    }
  };

  return (
    <div className="internal-login-container">
      <div className="internal-login-box">
        <div className="internal-login-header">
          <h1>🔐 Internal Access</h1>
          <p>Developer documentation and tools</p>
        </div>

        {error && <div className="error-message">{error}</div>}

        <form className="internal-login-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              required
              autoFocus
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              required
            />
          </div>

          <button type="submit" className="login-button" disabled={isLoading}>
            {isLoading ? "Logging in..." : "Login"}
          </button>
        </form>

        <div className="back-link">
          <a href="/">← Back to Home</a>
        </div>
      </div>
    </div>
  );
};

export default InternalLogin;
